import {MxSelector, MxSelectorOrSimply} from './store.types';
import {Observable, take} from 'rxjs';
import {inject} from '@angular/core';
import {createSelector, Store} from '@ngrx/store';
import {PickStringKeysByTypeFrom} from '@aviary/store/utility-type.helpers';
import {SelectOption, SelectOptions} from './common/common.types';
import {isSelector} from './common/typing.helpers';
import {asSameTypeOrUndefined, enumCaseToTitleCase} from './transformation.helpers';

/**
 * If you're calling this from a construction context, the store is automatically injected for you, so you can leave out the second argument.  For use in other
 * locations, you can inject the store yourself and pass it in.
 */
export function select<T>(selector: MxSelector<T>, store?: Store): Observable<T> {
  return (store ?? inject(Store)).select(selector);
}

/**
 * DON'T use this!!! ...unless you need to.  But check to make sure you REALLY can't do what you need to do without resorting to a snapshot.
 *
 * If you're calling this from a construction context, the store is automatically injected for you, so you can leave out the second argument.  For use in other
 * locations, you can inject the store yourself and pass it in.
 */
export function snapshot<T>(selector: MxSelector<T>, store?: Store): T {
  let value: T;

  select(selector, store).pipe(take(1)).subscribe(selected => value = selected);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- We know that the following @ts-ignore is safe.
  // @ts-ignore - We know that the value will be assigned before we get here because the above Store subscription is synchronous.
  return value;
}

/**
 * Given an array of some data object, returns an array of `{ displayName, value }` suitable for use with select form controls.
 *
 * @param objects The array of objects to transform.
 * @param displayNameKey The key from each object to use as the `displayName` value in the result.  (Must have string values.)
 * @param valueKey The key from each object to use as the `value` value in the result.
 * @param displayNameOverrides OPTIONAL - An object containing names to display instead of what is specified by displayNameKey.
 */
export function convertToSelectOptions<ObjectType extends object, ValueKey extends keyof ObjectType>(
  objects: ObjectType[], displayNameKey: PickStringKeysByTypeFrom<ObjectType, string>, valueKey: ValueKey, displayNameOverrides?: Map<string, string>
): SelectOptions<ObjectType[ValueKey]> {
  return objects.map(object => convertToSelectOption(object, displayNameKey, valueKey, displayNameOverrides));
}

/**
 * Given some data object, returns a `{ displayName, value }` object suitable for use as a select form control option.
 *
 * @param object The object to transform.
 * @param displayNameKey The key from the object to use as the `displayName` value in the result.  (Must have a string value.)
 * @param valueKey The key from the object to use as the `value` value in the result.
 * @param displayNameOverrides OPTIONAL - An object containing names to display instead of what is specified by displayNameKey.
 */
export function convertToSelectOption<ObjectType extends object, ValueKeyType extends keyof ObjectType>(
  object: ObjectType, displayNameKey: PickStringKeysByTypeFrom<ObjectType, string>, valueKey: ValueKeyType, displayNameOverrides?: Map<string, string>
): SelectOption<ObjectType[ValueKeyType]> {
  const displayName: string = object[displayNameKey] as string; // Yo, TypeScript!  This really is a string based on the typing, but you're not retaining that.

  return {
    displayName: displayNameOverrides?.get(displayName) ?? displayName,
    value: object[valueKey]
  };
}

export function stringArrayToSelectOptions<ValueType extends string>(stringArr: readonly ValueType[]): SelectOptions<ValueType> {
  return stringArr.map(value => ({
    displayName: enumCaseToTitleCase(value), value
  }));
}

// Overloads
export function asSelector<T>(valueOrSelector: MxSelectorOrSimply<T>): MxSelector<T>;
export function asSelector<T>(valueOrSelector?: MxSelectorOrSimply<T>): MxSelector<T | undefined>;

// Implementation
export function asSelector<T>(valueOrSelector?: MxSelectorOrSimply<T>): MxSelector<T | undefined> {
  return isSelector<T>(valueOrSelector)
    ? createSelector(valueOrSelector, asSameTypeOrUndefined)
    : createSelector(() => valueOrSelector);
}

/**
 * This creates a memoized selector that specifically returns MxSelector<string>, widening any narrow string union types.
 * Note that there are overloads for up to 8 inputs, just like NgRx has for createSelector().
 */
export function createStringSelector<S1>(
  s1: MxSelector<S1>,
  projector: (s1: S1) => string
): MxSelector<string>;

export function createStringSelector<S1, S2>(
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  projector: (s1: S1, s2: S2) => string
): MxSelector<string>;

export function createStringSelector<S1, S2, S3>(
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  projector: (s1: S1, s2: S2, s3: S3) => string
): MxSelector<string>;

export function createStringSelector<S1, S2, S3, S4>(
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  s4: MxSelector<S4>,
  projector: (s1: S1, s2: S2, s3: S3, s4: S4) => string
): MxSelector<string>;

export function createStringSelector<S1, S2, S3, S4, S5>(
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  s4: MxSelector<S4>,
  s5: MxSelector<S5>,
  projector: (s1: S1, s2: S2, s3: S3, s4: S4, s5: S5) => string
): MxSelector<string>;

export function createStringSelector<S1, S2, S3, S4, S5, S6>(
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  s4: MxSelector<S4>,
  s5: MxSelector<S5>,
  s6: MxSelector<S6>,
  projector: (s1: S1, s2: S2, s3: S3, s4: S4, s5: S5, s6: S6) => string
): MxSelector<string>;

export function createStringSelector<S1, S2, S3, S4, S5, S6, S7>( // NOSONAR: This has more arguments than Sonar wants, but we're matching NgRx overloads.
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  s4: MxSelector<S4>,
  s5: MxSelector<S5>,
  s6: MxSelector<S6>,
  s7: MxSelector<S7>,
  projector: (s1: S1, s2: S2, s3: S3, s4: S4, s5: S5, s6: S6, s7: S7) => string
): MxSelector<string>;

export function createStringSelector<S1, S2, S3, S4, S5, S6, S7, S8>( // NOSONAR: This has more arguments than Sonar wants, but we're matching NgRx overloads.
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  s4: MxSelector<S4>,
  s5: MxSelector<S5>,
  s6: MxSelector<S6>,
  s7: MxSelector<S7>,
  s8: MxSelector<S8>,
  projector: (s1: S1, s2: S2, s3: S3, s4: S4, s5: S5, s6: S6, s7: S7, s8: S8) => string
): MxSelector<string>;

// Unified implementation for all overloads.
export function createStringSelector(...args: unknown[]): MxSelector<string> {
  // Since we have 1–8 selectors + 1 projector, the last argument is always the projector.
  const selectors: MxSelector<unknown>[] = args.slice(0, -1) as MxSelector<unknown>[];
  const projector: (...inputs: unknown[]) => string = args[args.length - 1] as (...inputs: unknown[]) => string;

  return createSelector(...selectors, projector);
}

export function createTypedSelector<T, S1>(
  s1: MxSelector<S1>,
  projector: (s1: S1) => T
): MxSelector<T>;

export function createTypedSelector<T, S1, S2>(
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  projector: (s1: S1, s2: S2) => T
): MxSelector<T>;

export function createTypedSelector<T, S1, S2, S3>(
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  projector: (s1: S1, s2: S2, s3: S3) => T
): MxSelector<T>;

export function createTypedSelector<T, S1, S2, S3, S4>(
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  s4: MxSelector<S4>,
  projector: (s1: S1, s2: S2, s3: S3, s4: S4) => T
): MxSelector<T>;

export function createTypedSelector<T, S1, S2, S3, S4, S5>(
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  s4: MxSelector<S4>,
  s5: MxSelector<S5>,
  projector: (s1: S1, s2: S2, s3: S3, s4: S4, s5: S5) => T
): MxSelector<T>;

export function createTypedSelector<T, S1, S2, S3, S4, S5, S6>(
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  s4: MxSelector<S4>,
  s5: MxSelector<S5>,
  s6: MxSelector<S6>,
  projector: (s1: S1, s2: S2, s3: S3, s4: S4, s5: S5, s6: S6) => T
): MxSelector<T>;

export function createTypedSelector<T, S1, S2, S3, S4, S5, S6, S7>( // NOSONAR: This has more arguments than Sonar wants, but we're matching NgRx overloads.
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  s4: MxSelector<S4>,
  s5: MxSelector<S5>,
  s6: MxSelector<S6>,
  s7: MxSelector<S7>,
  projector: (s1: S1, s2: S2, s3: S3, s4: S4, s5: S5, s6: S6, s7: S7) => T
): MxSelector<T>;

export function createTypedSelector<T, S1, S2, S3, S4, S5, S6, S7, S8>( // NOSONAR: This has more arguments than Sonar wants, but we're matching NgRx overloads.
  s1: MxSelector<S1>,
  s2: MxSelector<S2>,
  s3: MxSelector<S3>,
  s4: MxSelector<S4>,
  s5: MxSelector<S5>,
  s6: MxSelector<S6>,
  s7: MxSelector<S7>,
  s8: MxSelector<S8>,
  projector: (s1: S1, s2: S2, s3: S3, s4: S4, s5: S5, s6: S6, s7: S7, s8: S8) => T
): MxSelector<T>;

// Unified implementation for all overloads.
export function createTypedSelector<T>(...args: unknown[]): MxSelector<T> {
  // Since we have 1–8 selectors + 1 projector, the last argument is always the projector.
  const selectors: MxSelector<unknown>[] = args.slice(0, -1) as MxSelector<unknown>[];
  const projector: (...inputs: unknown[]) => T = args[args.length - 1] as (...inputs: unknown[]) => T;

  return createSelector(...selectors, projector);
}
